/**
 * Accessibility fix (WCAG 2.2.2 — Pause, Stop, Hide)
 *
 * Several sections (section-double-v2.liquid, section-tab-collections-v2.liquid,
 * section-text-row-bcorp.liquid, and any future [data-slider] section) enable
 * Flickity's autoPlay option with no way for the user to stop it. This script
 * listens for the theme's own "theme:slider:loaded" event (dispatched by the
 * shared Slider class in theme.js on every Flickity instance) and, if that
 * slider has autoPlay enabled, injects a small accessible pause/play toggle
 * button. It also permanently stops autoplay up front if the user has
 * prefers-reduced-motion set.
 *
 * This is intentionally a separate, additive file rather than an edit to the
 * minified theme.js bundle, so it's easy to review, remove, or fold into the
 * main bundle later without hand-editing minified code.
 */
(function () {
  'use strict';

  var STYLE_ID = 'slider-autoplay-toggle-styles';
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var processed = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var processedFallback = [];

  function alreadyProcessed(el) {
    if (processed) return processed.has(el);
    return processedFallback.indexOf(el) !== -1;
  }

  function markProcessed(el) {
    if (processed) { processed.add(el); return; }
    processedFallback.push(el);
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      /* Hidden by default so it doesn't change the visual design for
         everyday mouse browsing -- this is a keyboard-accessibility
         affordance, not a mouse-discoverable control, so it only reveals
         on keyboard focus, never on hover. It is always present for
         screen readers regardless of visual state, since opacity/
         pointer-events don't remove it from the accessibility tree or
         tab order. Vertically centered (rather than bottom-anchored) so
         it sits correctly in both tall hero sliders and short strips
         like the announcement bar, instead of assuming a fixed height. */
      '.slider-autoplay-toggle{position:absolute;z-index:5;top:50%;right:8px;' +
      'transform:translateY(-50%);' +
      'width:28px;height:28px;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(255,255,255,0.9);border:1px solid rgba(0,0,0,0.15);border-radius:50%;' +
      'cursor:pointer;padding:0;opacity:0;transition:opacity 0.15s ease;}' +
      '.slider-autoplay-toggle:focus,' +
      '.slider-autoplay-toggle:focus-visible{opacity:1;}' +
      '.slider-autoplay-toggle:focus-visible{outline:2px solid #000;outline-offset:2px;}' +
      '.slider-autoplay-toggle__icon{width:12px;height:12px;display:block;}';
    document.head.appendChild(style);
  }

  function sliderHasAutoplay(slideshow) {
    var optionsAttr = slideshow.getAttribute('data-options');
    if (!optionsAttr) return false;
    try {
      var opts = JSON.parse(decodeURIComponent(optionsAttr));
      return Boolean(opts.autoPlay);
    } catch (e) {
      return false;
    }
  }

  function buildToggle(slideshow, flkty) {
    if (alreadyProcessed(slideshow)) return;
    markProcessed(slideshow);
    injectStyles();

    var wrapper = slideshow.parentElement;
    if (wrapper) {
      var computedPosition = window.getComputedStyle(wrapper).position;
      if (computedPosition === 'static') {
        wrapper.style.position = 'relative';
      }
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'slider-autoplay-toggle';
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', 'Pause automatic slide rotation');
    btn.innerHTML =
      '<svg class="slider-autoplay-toggle__icon slider-autoplay-toggle__icon--pause" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="6" y="5" width="4" height="14" fill="currentColor"/><rect x="14" y="5" width="4" height="14" fill="currentColor"/></svg>' +
      '<svg class="slider-autoplay-toggle__icon slider-autoplay-toggle__icon--play" viewBox="0 0 24 24" aria-hidden="true" focusable="false" hidden><path d="M7 4l14 8-14 8V4z" fill="currentColor"/></svg>';

    (wrapper || slideshow).appendChild(btn);

    var pauseIcon = btn.querySelector('.slider-autoplay-toggle__icon--pause');
    var playIcon = btn.querySelector('.slider-autoplay-toggle__icon--play');
    var isPaused = false;

    function setPaused(state) {
      isPaused = state;
      btn.setAttribute('aria-pressed', String(state));
      btn.setAttribute('aria-label', state ? 'Play automatic slide rotation' : 'Pause automatic slide rotation');
      if (pauseIcon) pauseIcon.hidden = state;
      if (playIcon) playIcon.hidden = !state;
      if (!flkty) return;
      if (state) {
        if (typeof flkty.stopPlayer === 'function') flkty.stopPlayer();
      } else {
        if (typeof flkty.playPlayer === 'function') flkty.playPlayer();
      }
    }

    btn.addEventListener('click', function () {
      setPaused(!isPaused);
    });

    if (prefersReducedMotion) {
      setPaused(true);
    }
  }

  document.addEventListener('theme:slider:loaded', function (evt) {
    var sliderInstance = evt.detail && evt.detail.slider;
    if (!sliderInstance || !sliderInstance.slideshow || !sliderInstance.flkty) return;
    if (!sliderHasAutoplay(sliderInstance.slideshow)) return;
    buildToggle(sliderInstance.slideshow, sliderInstance.flkty);
  });

  // Exposed so other autoplaying carousels that are not Flickity instances
  // (e.g. section-press-logos.liquid's setInterval-based rotation) can reuse
  // the same injected `.slider-autoplay-toggle` styling instead of
  // duplicating it.
  window.themeSliderAutoplayToggle = { injectStyles: injectStyles };
})();
